import { useMediaQuery } from '@mui/material';
import { IBudgetItem } from '../../createAndEdit/types';
import { parseDiscountPercent } from '../../createAndEdit/utils/budgetCalculations';
import DetailServiceItemRow from './DetailServiceItemRow';
import DetailServiceItemsTableMobile from './mobile/DetailServiceItemsTableMobile';

interface IDetailServiceItemsTableProps {
  items: IBudgetItem[];
  serviceSubtotal?: number;
}

const DetailServiceItemsTable = (props: IDetailServiceItemsTableProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const parseQuantityToNumber = (qty: string | number | undefined): number => {
    if (qty === undefined || qty === null) return 0;
    const qtyStr = String(qty);
    const clean = qtyStr.replace(/[^\d.,]/g, '').replace(',', '.');
    return Number.parseFloat(clean) || 0;
  };

  const visibleItems = (props.items || []).filter((item) => {
    if (item.removed === true) return false;
    const quantity = parseQuantityToNumber(item.quantity);
    return quantity > 0;
  });
  
  const calculateItemSubtotal = (item: IBudgetItem, onlyCovered: boolean = false) => {
    const itemValue = (onlyCovered && item.isCovered === true) || !onlyCovered ? (item.value || 0) : 0;
    let clean = String(item.quantity || '0').replace(/[^\d.,]/g, '');
    clean = clean.replace(',', '.');
    const quantityParsed = Number.parseFloat(clean);
    const quantity = Number.isNaN(quantityParsed) ? 0 : quantityParsed;
    return itemValue * quantity * (1 - parseDiscountPercent(item.discount) / 100);
  }

  const calculatedSubtotal = () => {
    return visibleItems.reduce((sum, item) => sum + calculateItemSubtotal(item, false), 0);
  };

  const calculatedTruckerPayPlanSubtotal = () => {
    return visibleItems.reduce((sum, item) => sum + calculateItemSubtotal(item, true), 0);
  };

  if (isMobile) {
    return (
      <DetailServiceItemsTableMobile
        items={props.items}
        serviceSubtotal={props.serviceSubtotal}
      />
    );
  }


  const classNames = {
    header: 'font-semibold text-size-12 text-gray-600',
    body: 'text-size-14 text-gray-500',
  };
  return (
    <div className="overflow-x-auto">
      <table
        className="table table-layout-fixed table-column-overflow-hidden table-head-filled mb-3 w-full [&_td]:border-0 [&_th]:border-0 [&_tbody_tr]:border-0 [&_tbody_tr]:border-b-0 [&_thead_tr]:border-0 [&_thead_tr]:border-b-0 [&_tbody_tr_td]:border-0 [&_tbody_tr_td]:border-b-0 [&_thead_tr_th]:border-0 [&_thead_tr_th]:border-b-0"
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
      <thead>
        <tr className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
          <th className="w-[7%] border-0 !border-b-0 " style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Ref.</span>
          </th>
          <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Item</span>
          </th>
          <th className="w-[26%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Descrição</span>
          </th>
          <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Quantidade</span>
          </th>
          <th className="w-[12%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Valor (R$)</span>
          </th>
          <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>% Desconto</span>
          </th>
          <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Cobertura</span>
          </th>
          <th className="w-[15%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <span className={classNames.header}>Subtotal (R$)</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {visibleItems.map((item, index) => (
          <DetailServiceItemRow key={`item-${item.ref}-${index}`} item={item} />
        ))}
      </tbody>
      {typeof props.serviceSubtotal === 'number' && (
        <tfoot>
          <tr className="w-full border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            <td className="border-0 !border-b-0 text-right" colSpan={8} style={{ borderBottom: 'none' }}>
              <span className="text-gray-600 mr-2 text-size-12">Subtotal do serviço:</span>
              <span className="text-gray-600 pr-2 text-size-12">{formatCurrency(calculatedSubtotal())}</span>
              {
                calculatedTruckerPayPlanSubtotal() > 0 ? (
                  <div>
                    <span className="text-[#5CB85C] mr-2 text-size-12">Valor aprovado plano de manutenção:</span>
                    <span className="text-[#5CB85C] pr-2 text-size-12">{formatCurrency(calculatedTruckerPayPlanSubtotal())}</span>
                  </div>
                ) : null
              }
            </td>
          </tr>
        </tfoot>
      )}
    </table>
    </div>
  );
};

export default DetailServiceItemsTable;
